import React, {Fragment, useEffect} from "react";
import {useRouter} from "next/router";
import shopApi from "../../../api/shop";
import {IPageProps} from "../../../types/pages";
import {domainUrl} from "../../../helper";
import {NextPage} from "next";
import PostInnerPage from "../../../components/posts/PostInnerPage";
import {IPost, IPostCategory, ITag} from "../../../types/posts";
import Head from "next/head";
import MainLayout from "../../../components/ForCWV/Wrappers/MainLayout";
import {BlogPageTranslations} from "../../../services/translationKeys";
import {layoutProps} from "../../../services/layoutProps";
import {TranslationsType} from "../../../types/homePageTypes";

interface IProps extends IPageProps {
    post: IPost,
    postCategory: IPostCategory
    postTags: Array<ITag>
    translationsData: TranslationsType;
    siteName: string
}

const Post: NextPage<IProps> = (pageProps) => {
    const {
        domain,
        dbName,
        post,
        postTags,
        breadcrumb,
        selectedLocale,
        postCategory,
        translationsData,
        siteName,
        defaultLangCode,
        locales
    } = pageProps
    const {
        meta_title = "",
        meta_description = "",
        meta_keywords = "",
        meta_image = ""
    } = post
    const router = useRouter();
    const meta_url = domainUrl(`${dbName}${router.locale !== "catchAll" && router.locale !== defaultLangCode ? "/" + selectedLocale : ""}${router.asPath}`)
    const m_img = domainUrl(`${dbName}/storage/${domain}/${meta_image}`)

    useEffect(() => {
        window.onpopstate = () => {
            router.push(window.location.pathname)
        }
    }, [router.query.postSlug, selectedLocale, router.route])

    return (
        <MainLayout {...pageProps}>
            <Fragment>
                <Head>
                    <title>{meta_title}</title>
                    <meta name="title" content={meta_title}/>
                    <meta name="description" content={meta_description}/>
                    <meta name="keywords" content={meta_keywords}/>
                    <meta name="generator" content="Zegashop.com"/>
                    <meta name="robots" content="index, follow"/>
                    <meta name="twitter:description" content={meta_description}/>
                    <meta name="twitter:title" content={meta_title}/>
                    <meta name="twitter:image" content={m_img}/>
                    <meta property="og:description" content={meta_description}/>
                    <meta property="og:title" content={meta_title}/>
                    <meta property="og:keywords" content={meta_keywords}/>
                    <meta property="og:url" content={meta_url}/>
                    <meta property="og:locale" content={selectedLocale}/>
                    <meta property="og:image" content={m_img}/>
                    <meta property="og:site_name" content={siteName}/>
                    <link rel="canonical" href={meta_url}/>
                    {locales?.length > 0 ? (
                        <>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang="x-default"/>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang={defaultLangCode}/>
                            {locales.map(intl => (
                                <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}${router.asPath}`)} hrefLang={intl.code}/>
                            ))}
                        </>
                    ) : null}
                </Head>
                <PostInnerPage
                    dbName={dbName}
                    postSlug={post.slug}
                    locale={selectedLocale}
                    post={post}
                    postCategory={postCategory}
                    postTags={postTags}
                    domain={domain}
                    breadcrumb={breadcrumb}
                    translationsData={translationsData}
                />
            </Fragment>
        </MainLayout>
    );
}

export async function getServerSideProps({locale, req, res, query}: any) {
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: BlogPageTranslations,
        ipv4: typedReq.ipv4,
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, translationsData: {Home: HomeIntl, Posts: PostsIntl} } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }
    const dbName = req.defaultDB.db_name
    const {postSlug, categorySlug} = query;

    let post = null;
    let selectedCategory = null;
    let postTags = null;

    if (postSlug !== "undefined") {
        const postSlugOptions = {
            slug: postSlug,
            locale: selectedLocale,
            dbName
        }
        post = await shopApi.getAsyncPostsBySlug(postSlugOptions, req.m)
        if (post.notFound) {
            return {
                notFound: true
            }
        }
        postTags = await shopApi.getAsyncTagsBySlug({locale, slug: postSlug}, req.m)
    }

    if (categorySlug !== "undefined") {
        selectedCategory = await shopApi.getAsyncPostsCatBySlug({locale, slug: categorySlug, dbName}, req.m)
    }

    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: PostsIntl, url: "/posts"},
        {title: selectedCategory.name, url: `/posts/${selectedCategory.slug}`},
        {title: post.name, url: ""}
    ]

    return {
        props: {
            breadcrumb,
            locale: selectedLocale,
            post,
            postCategory: selectedCategory,
            postTags,
            ...wrapperProps,
        },
    };
}

export default Post